import {MimeType} from "@reva/shared/mime-type";
import type {RevaValuationPurpose} from "@reva/shared/valuation-purpose";

import type ServiceApi from "../../api";
import {AvmApi} from "../api-constants";
import type {AvmDocumentedEndpoint} from "../api-constants-avm";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";
import {FileDownloadApiInterface} from "./file-download";

/**
 * Common base for the documentation PDFs of the AVM API. Subclasses contribute the endpoint path
 * and, if the endpoint takes any, the query parameters.
 */
abstract class AvmDocumentationApiInterface extends FileDownloadApiInterface {
    /** Die Doku-Endpunkte der AVM-API liefern ausschließlich PDF und antworten sonst mit 406. */
    protected acceptMimeType(): MimeType {
        return MimeType.PDF;
    }

    /** Path of the documentation endpoint, relative to the AVM API base URL. */
    protected abstract documentPath(): string;

    /** Query parameters of the request; undefined values are left out of the URL. */
    protected queryParams(): Record<string, string | undefined> | undefined {
        return undefined;
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildAvmApiUrl([this.documentPath()], this.queryParams());
    }
}

/** API interface for the REVA timelines documentation (GET {@link AvmApi.PATH_TIMELINES_DOCUMENTATION}). */
export class TimelinesDocumentationApiInterface extends AvmDocumentationApiInterface {
    protected documentPath(): string {
        return AvmApi.PATH_TIMELINES_DOCUMENTATION;
    }
}

/** API interface for the general AVM documentation (GET {@link AvmApi.PATH_AVM_DOCUMENTATION}). */
export class AvmGeneralDocumentationApiInterface extends AvmDocumentationApiInterface {
    protected documentPath(): string {
        return AvmApi.PATH_AVM_DOCUMENTATION;
    }
}

/** API interface for the AVM release notes (GET {@link AvmApi.PATH_AVM_RELEASENOTES}). */
export class AvmReleaseNotesApiInterface extends AvmDocumentationApiInterface {
    protected documentPath(): string {
        return AvmApi.PATH_AVM_RELEASENOTES;
    }
}

/** API interface for the AVM validation report (GET {@link AvmApi.PATH_AVM_VALIDATION_REPORT}). */
export class AvmValidationReportApiInterface extends AvmDocumentationApiInterface {
    protected documentPath(): string {
        return AvmApi.PATH_AVM_VALIDATION_REPORT;
    }
}

/**
 * API interface for the method documentation of the residential locations
 * (micro/macro location, GET {@link AvmApi.PATH_MICRO_MACRO_DOCUMENTATION}).
 */
export class MicroMacroDocumentationApiInterface extends AvmDocumentationApiInterface {
    protected documentPath(): string {
        return AvmApi.PATH_MICRO_MACRO_DOCUMENTATION;
    }
}

/**
 * API interface for the AVM parameter overview (GET {@link AvmApi.PATH_AVM_PARAMETERS}).
 * The AVM API answers in English without the language parameter.
 */
export class AvmParametersApiInterface extends AvmDocumentationApiInterface {
    constructor(private readonly language: "de" | "en") {
        super();
    }

    protected documentPath(): string {
        return AvmApi.PATH_AVM_PARAMETERS;
    }

    protected queryParams(): Record<string, string | undefined> {
        return {language: this.language};
    }
}

/**
 * API interface for the REVA user manual of one purpose (GET {@link AvmApi.PATH_REVA_MANUAL}).
 * The AVM API falls back to German if the manual is not available in the requested language.
 */
export class RevaManualApiInterface extends AvmDocumentationApiInterface {
    constructor(
        private readonly purpose: RevaValuationPurpose,
        private readonly language: string,
    ) {
        super();
    }

    protected documentPath(): string {
        return AvmApi.PATH_REVA_MANUAL;
    }

    protected queryParams(): Record<string, string | undefined> {
        return {purpose: this.purpose, language: this.language};
    }
}

/**
 * API interface for the method documentation of a single indication endpoint
 * (GET {@link AvmApi.PATH_INDICATION_DOCUMENTATION}). The AVM API only delivers it if the licence
 * covers the endpoint itself, otherwise 403.
 */
export class IndicationDocumentationApiInterface extends AvmDocumentationApiInterface {
    constructor(private readonly endpoint: AvmDocumentedEndpoint) {
        super();
    }

    protected documentPath(): string {
        return AvmApi.PATH_INDICATION_DOCUMENTATION[this.endpoint];
    }
}

/**
 * API interface for the list of AVM documentations with their upload date
 * (GET {@link AvmApi.PATH_AVM_DOCUMENTATION_LIST}). Unlike its siblings this one is a JSON request,
 * not a download: it answers with what {@link import("../../util/revaManuals").parseDocumentationList}
 * expects, an array of `AvmDocumentationEntry`.
 */
export class AvmDocumentationListApiInterface extends SimpleApiInterface<unknown> {
    protected buildUrl(api: ServiceApi): string {
        return api.buildAvmApiUrl([AvmApi.PATH_AVM_DOCUMENTATION_LIST]);
    }

    protected async executeInternal(executor: ApiExecutor, url: string, credentials: ApiCredentials): Promise<unknown> {
        return executor.fetchGET(url, credentials);
    }
}
